import { defineComponent, h } from 'vue';
import { Vue } from '@/Shared/vueCompat.js';
import { QSelect } from 'quasar';
import BaseComponent from './BaseComponentMixin.jsx';
import careHelpfulFunctions from '../careHelpfulFunctions.jsx';

import utils from '../../Shared/utils.jsx';

export default defineComponent({
    name: 'combo-box',
    components: { QSelect },
    mixins: [BaseComponent],
    data: function () {
        return {
            thisproxy: null,
            publishFieldEval: null,
            setValueFunction: null,
            sourceUrlEval: null,
            sourceRawInterpolatedEval: null,
            data: [],
            model: '',
            inputText: '',
            filter: '',
        }
    },
    props: {},
    watch: {
        sourceUrl: async function() {
            this.data = await utils.api.get(utils.evaluate(this.sourceUrlEval, this));
        }
    },
    computed: {
        publishField: {
            set(value){
                if(this.setValueFunction === null && this.controlData.PublishField) {
                    let vueSet = utils.helpers.convertSetValueToVueSet(this.controlData.PublishField, 'value');
                    this.setValueFunction = new Function('value', 'context', 'util', 'vue', `with (context) { ${vueSet} }`);
                    this.thisproxy = utils.helpers.getWithProxy(this);
                }
                if (this.setValueFunction) {
                    this.setValueFunction(value, this.thisproxy.proxy, careHelpfulFunctions, Vue)
                }
                this.model = value;
            },
            get() {
                return this.model;
            }
        },
        sourceUrl() {
            return utils.evaluate(this.sourceUrlEval, this);
        },
        sourceRawInterpolated(){
            return utils.evaluateObject(this.sourceRawInterpolatedEval, this);
        },
        sourceData() {
            switch(this.controlData.SourceType.toLowerCase()) {
                case 'url':
                case 'raw': 
                    return this.data;
                case 'rawinterpolated': return this.sourceRawInterpolated;
                default: return [];
            }
        },
        styles() {
            return {
                ...utils.resolveStyleHints(this.styleHints, this),
                ...this.sizeStyle
            }
        },
        normalizedOptions() {
            if (!Array.isArray(this.sourceData))
                return [];

            const items = this.sourceData.map(item => ({
                label: this.optionLabel(item),
                value: (item && typeof item === 'object' && this.controlData.ValueField)
                    ? item[this.controlData.ValueField]
                    : item,
            }));

            if (this.filter)
                return items.filter(a => a.label.toLowerCase().includes(this.filter.toLowerCase()));
            else
                return items;
        },
        selectedOptionLabel() {
            const matched = this.normalizedOptions.find(o => o.value == this.model);
            if (matched)
                return matched.label;
            if (typeof this.model === 'string')
                return this.model;
            return '';
        }
    },
    //Created Replaced with preRenderComplete
    methods: {
        // The combobox does not support the item-value prop, so the on input event emits the entire item. If the item is only 
        // a string, we can just save it off. If we have a value field, we can get that from the object. If we don't have anything, we'll just 
        // save the whole object.
        onInput(item) {
            if (typeof item !== 'string' && this.inputText && this.inputText !== this.selectedOptionLabel) {
                this.publishField = this.inputText;
                return;
            }
            if(typeof item === 'string')
                this.publishField = item;
            else 
                this.publishField = item;

            this.inputText = this.selectedOptionLabel;
        },
        commitCustomInput(rawValue) {
            const typedValue = typeof rawValue === 'string' ? rawValue : this.inputText;
            if (typeof typedValue === 'string' && typedValue !== '' && typedValue !== this.selectedOptionLabel) {
                this.publishField = typedValue;
                this.inputText = typedValue;
            }
        },
        onInputText(value) {
            this.inputText = value || '';
        },
        onBlurInput(e) {
            this.commitCustomInput(e?.target?.value);
        },
        onKeydownInput(e) {
            if (e.key === 'Tab')
                this.commitCustomInput(e?.target?.value);
        },
        optionLabel(item) {
            if (item == null) return '';
            if (typeof item === 'string') return item;
            if (this.controlData.DisplayField && item[this.controlData.DisplayField] != null)
                return String(item[this.controlData.DisplayField]);
            return String(item);
        },
        async preRenderComplete() {
            switch(this.controlData.SourceType.toLowerCase()) {
                case 'url': {
                    this.sourceUrlEval = utils.compile(this, this.controlData.SourceURL);
                    this.data = await utils.api.get(this.sourceUrl);
                    break;
                }
                case 'raw': {
                    this.data = this.controlData.SourceRaw;
                    break;
                }
                case 'rawinterpolated': {
                    this.sourceRawInterpolatedEval = utils.compileObject(this, this.controlData.SourceRaw);
                    break;
                }
            }
            this.inputText = this.selectedOptionLabel;
            this.finishRenderHandler(this);
        },
        filterFn(val, update) {
            update(() => this.filter = val);
        },
    },
    render() {
        if (!this.todisplay)
                return null;
        try {
            // ensure control fits within ag-grid
            const isContentDef = this.parentType === 'ContentDef';
            const containerClass = isContentDef ? 'q-ma-none' : 'q-ma-xs';

            return (
                <div
                    class={{ 'c-ComboBox': true, [`c-name-${this.name || 'unnamed'}`]: true, [containerClass]: true }}
                    style={this.styles}
                    v-show={this.isvisible}>
                    <QSelect
                        options={this.normalizedOptions}
                        optionLabel="label"
                        optionValue="value"
                        emitValue
                        mapOptions
                        input-debounce={0}
                        popup-content-class="q-select-max-popup-height"
                        modelValue={this.model}
                        inputValue={this.inputText}
                        placeholder={this.controlData.PlaceHolder}
                        onUpdate:modelValue={this.onInput}
                        onInputValue={this.onInputText}
                        onNewValue={this.onInputText}
                        onBlur={this.onBlurInput}
                        onKeydown={this.onKeydownInput}
                        useInput
                        fillInput
                        onFilter={this.filterFn}
                        newValueMode="add-unique"
                        hideSelected
                        hideBottomSpace
                        dense
                        outlined
                    ></QSelect>
                </div>
            );
        }
        catch (e) {
            utils.error('ComboBox Render failed', e);
            return <div>ComboBox Failed to Render {e}</div>;
        }
    }
});